import { useState } from "react";
import { KeyRound, ShieldCheck, Users } from "lucide-react";

const ACCESS_LABELS = {
  read: "Read",
  read_write: "Read / write",
  operate: "Operate",
  manage: "Manage",
  audit: "Audit",
};

const TIER_COLORS = {
  frontend: "#5B5BD6",
  compute: "#0EA5E9",
  data: "#0D9488",
  networking: "#F59E0B",
  security: "#EF4444",
};

function Permission({ children }) {
  return (
    <span
      className="font-mono"
      style={{
        fontSize: 10,
        padding: "3px 7px",
        borderRadius: 999,
        background: "var(--color-background-tertiary)",
        border: "1px solid var(--color-border-primary)",
        color: "var(--color-text-secondary)",
      }}
    >
      {children}
    </span>
  );
}

/**
 * The identity tab is a permission map, rather than forcing an RBAC document into
 * the hierarchy canvas. It is intentionally read-only: groups and bindings are
 * generated from the architecture and remain separate from IaC resources.
 */
export default function RbacView({ plan, nodes, provider }) {
  const [showTechnicalIds, setShowTechnicalIds] = useState(false);
  const groups = Array.isArray(plan?.groups) ? plan.groups : [];
  const roles = Array.isArray(plan?.roles) ? plan.roles : [];
  const bindings = Array.isArray(plan?.bindings) ? plan.bindings : [];
  const nodeById = new Map((nodes || []).map((node) => [node.id, node]));
  const referencedIds = [...new Set(bindings.flatMap((binding) => binding.resource_ids || []))]
    .sort((a, b) => b.length - a.length);
  const resourceName = (id) => {
    const node = nodeById.get(id);
    return node?.name || node?.label || "Resource unavailable";
  };
  const humanize = (value) => {
    const text = String(value || "");
    let cursor = 0;
    let output = "";
    while (cursor < text.length) {
      let foundAt = -1;
      let foundId = null;
      for (const id of referencedIds) {
        if (!id) continue;
        let at = text.indexOf(id, cursor);
        while (at !== -1) {
          const before = at > 0 ? text[at - 1] : "";
          const after = text[at + id.length] || "";
          if ((!before || !/[a-zA-Z0-9_-]/.test(before)) &&
              (!after || !/[a-zA-Z0-9_-]/.test(after))) break;
          at = text.indexOf(id, at + 1);
        }
        if (at !== -1 && (foundAt === -1 || at < foundAt ||
            (at === foundAt && id.length > foundId.length))) {
          foundAt = at;
          foundId = id;
        }
      }
      if (foundAt === -1) break;
      output += text.slice(cursor, foundAt) + resourceName(foundId);
      cursor = foundAt + foundId.length;
    }
    return output + text.slice(cursor);
  };
  const rolesByGroup = new Map();
  const bindingsByRole = new Map();

  roles.forEach((role) => {
    const current = rolesByGroup.get(role.group_id) || [];
    current.push(role);
    rolesByGroup.set(role.group_id, current);
  });
  bindings.forEach((binding) => {
    const current = bindingsByRole.get(binding.role_id) || [];
    current.push(binding);
    bindingsByRole.set(binding.role_id, current);
  });

  return (
    <section
      data-testid="rbac-view"
      aria-label="Identity and access plan"
      style={{
        height: "100%",
        overflow: "auto",
        padding: "72px 32px 32px",
        background: "var(--color-background-primary)",
      }}
    >
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>
        <div className="flex items-start justify-between gap-5" style={{ marginBottom: 24 }}>
          <div>
            <div
              className="flex items-center gap-2 font-mono"
              style={{ color: "var(--color-accent)", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}
            >
              <ShieldCheck size={15} /> LEAST-PRIVILEGE ACCESS PLAN
            </div>
            <h2 style={{ color: "var(--color-text-primary)", fontSize: 25, fontWeight: 700, letterSpacing: "-0.025em", marginTop: 8 }}>
              Identity &amp; access
            </h2>
            <p style={{ color: "var(--color-text-secondary)", fontSize: 13, lineHeight: 1.6, marginTop: 6, maxWidth: 680 }}>
              {provider === "azure"
                ? "Microsoft Entra groups and managed identities receive Azure RBAC roles scoped to the resources they need. This is a design recommendation, not deployed permissions."
                : "Groups receive roles scoped to the resources they need. This is a generated design recommendation, not deployed permissions."}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="font-mono"
              style={{
                flexShrink: 0,
                fontSize: 10,
                padding: "6px 9px",
                borderRadius: 999,
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-tertiary)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Read-only overlay
            </span>
            <button type="button" onClick={() => setShowTechnicalIds((value) => !value)}
              style={{ fontSize: 11, color: "var(--color-text-secondary)", textDecoration: "underline" }}>
              {showTechnicalIds ? "Hide technical IDs" : "Show technical IDs"}
            </button>
          </div>
        </div>

        {groups.length === 0 ? (
          <div
            style={{
              border: "1px dashed var(--color-border-primary)",
              borderRadius: 12,
              padding: 24,
              color: "var(--color-text-secondary)",
              fontSize: 13,
            }}
          >
            No RBAC plan is available for this project yet.
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))" }}>
            {groups.map((group) => (
              <article
                key={group.id}
                data-testid={`rbac-group-${group.id}`}
                style={{
                  background: "var(--color-background-secondary)",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: 12,
                  padding: 18,
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex items-center justify-center"
                    style={{ width: 30, height: 30, borderRadius: 8, background: "color-mix(in oklab, var(--color-accent) 14%, transparent)", color: "var(--color-accent)", flexShrink: 0 }}
                  >
                    <Users size={15} />
                  </div>
                  <div>
                    <h3 style={{ color: "var(--color-text-primary)", fontSize: 14, fontWeight: 650 }}>{humanize(group.name)}</h3>
                    <p style={{ color: "var(--color-text-tertiary)", fontSize: 12, lineHeight: 1.5, marginTop: 3 }}>{humanize(group.description)}</p>
                  </div>
                </div>

                {(rolesByGroup.get(group.id) || []).map((role) => (
                  <div key={role.id} style={{ borderTop: "1px solid var(--color-border-primary)", marginTop: 16, paddingTop: 14 }}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <KeyRound size={13} color="var(--color-text-secondary)" />
                        <span style={{ color: "var(--color-text-primary)", fontSize: 13, fontWeight: 600 }}>{humanize(role.name)}</span>
                      </div>
                    </div>
                    <p style={{ color: "var(--color-text-secondary)", fontSize: 12, lineHeight: 1.5, marginTop: 6 }}>{humanize(role.description)}</p>
                    <div className="flex flex-wrap gap-1.5" style={{ marginTop: 9 }}>
                      {(role.permissions || []).map((permission) => <Permission key={permission}>{permission}</Permission>)}
                    </div>

                    {(bindingsByRole.get(role.id) || []).map((binding) => (
                      <div
                        key={binding.id}
                        data-testid="rbac-binding"
                        style={{ marginTop: 12, padding: 11, borderRadius: 8, background: "var(--color-background-tertiary)" }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-mono" style={{ color: "var(--color-accent)", fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                            {ACCESS_LABELS[binding.access] || binding.access}
                          </span>
                          <span style={{ color: "var(--color-text-tertiary)", fontSize: 11 }}>{(binding.resource_ids || []).length} resource{(binding.resource_ids || []).length === 1 ? "" : "s"}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5" style={{ marginTop: 8 }}>
                          {(binding.resource_ids || []).map((resourceId) => {
                            const resource = nodeById.get(resourceId);
                            const tier = (resource?.config?.tier || resource?.tier || "").toLowerCase();
                            return (
                              <span
                                key={resourceId}
                                style={{
                                  fontSize: 11,
                                  padding: "3px 7px",
                                  borderRadius: 6,
                                  color: TIER_COLORS[tier] || "var(--color-text-secondary)",
                                  background: "var(--color-background-secondary)",
                                  border: "1px solid var(--color-border-primary)",
                                }}
                              >
                                {resourceName(resourceId)}
                                {showTechnicalIds && <code style={{ display: "block", fontSize: 9 }}>
                                  {resourceId}
                                </code>}
                              </span>
                            );
                          })}
                        </div>
                        {binding.rationale && <p style={{ color: "var(--color-text-tertiary)", fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}>{humanize(binding.rationale)}</p>}
                      </div>
                    ))}
                  </div>
                ))}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
